/**
 * useWakeWord — continuously-listening wake-phrase detector.
 *
 * JARVIS-style trigger: a low-priority `SpeechRecognition` instance
 * runs in `continuous: true` mode, and when the transcript contains
 * one of the configured wake phrases (default: "hey arc", "okay arc",
 * "ok arc", "arc activate", "arc"), the supplied `onWake(rest)`
 * callback fires with whatever the user said *after* the phrase.
 *
 * Why client-side: the wake phrase is short, ambient noise is high,
 * and a server roundtrip would defeat the "instant" feel. The browser
 * already has SpeechRecognition; we just give it a tighter activation
 * loop than push-to-talk.
 *
 * Caveats:
 *   - Only Chrome/Edge support `webkitSpeechRecognition`. Other browsers
 *     get `supported: false` and the hook no-ops.
 *   - Browser auto-stops recognition every ~60s of silence. We restart
 *     it from `onend` while `enabled` is true.
 *   - Microphone permission is required — the caller's UI should explain
 *     this before flipping the toggle.
 *   - When the page is hidden (Page Visibility API), we pause to avoid
 *     burning battery + bandwidth on tab the user isn't using.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

// Re-using the same minimal typing as useVoiceIO — the lib.dom shapes
// don't include SpeechRecognition.
interface SRResult {
  [index: number]: { transcript: string; confidence: number }
  isFinal: boolean
  length: number
}
interface SREvent extends Event {
  resultIndex: number
  results: { [index: number]: SRResult; length: number }
}
interface SRInstance extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((this: SRInstance, ev: SREvent) => void) | null
  onerror: ((this: SRInstance, ev: Event) => void) | null
  onend: ((this: SRInstance, ev: Event) => void) | null
  onstart: ((this: SRInstance, ev: Event) => void) | null
}
type SRCtor = new () => SRInstance

// 2026-07-02 — "Ryekronix" is the primary customer-facing wake phrase.
// Legacy "arc" phrases are kept as fallbacks so the recogniser still
// triggers if a user says "hey arc" out of habit OR if the ASR model
// mishears the 3-syllable brand name as something arc-adjacent.
const DEFAULT_WAKE_PHRASES = [
  'hey ryekronix',
  'okay ryekronix',
  'ok ryekronix',
  'ryekronix activate',
  'hi ryekronix',
  'hello ryekronix',
  'hey arc',
  'okay arc',
  'ok arc',
  'arc activate',
  'hi arc',
  'hello arc',
]

interface WakeWordOptions {
  enabled: boolean
  /** Fires when a wake phrase is detected. `rest` is whatever followed. */
  onWake: (rest: string) => void
  /** Override / extend the default trigger list. */
  phrases?: string[]
  lang?: string
  /** Caller hook: surface non-routine errors (perm denied, etc.). */
  onError?: (msg: string) => void
}

interface WakeWordReturn {
  supported: boolean
  /** True while recognition is actively running. */
  active: boolean
}

/**
 * Strip a wake phrase off the front of the transcript and return the
 * remainder. If no wake phrase matches, return null.
 */
function matchWake(text: string, phrases: string[]): string | null {
  const t = text.trim().toLowerCase()
  for (const p of phrases) {
    const pl = p.toLowerCase()
    // Allow leading filler ("um, hey arc ...") by checking includes() too,
    // but only honour matches near the start of the utterance to avoid
    // catching "...is the arc..." mid-sentence.
    const idx = t.indexOf(pl)
    if (idx >= 0 && idx <= 6) {
      return t.slice(idx + pl.length).replace(/^[\s,.:;!?-]+/, '').trim()
    }
  }
  return null
}

export default function useWakeWord(opts: WakeWordOptions): WakeWordReturn {
  const { enabled, onWake, phrases = DEFAULT_WAKE_PHRASES, lang = 'en-US', onError } = opts
  const recRef = useRef<SRInstance | null>(null)
  const wantRunningRef = useRef<boolean>(false)
  const [supported, setSupported] = useState<boolean>(false)
  const [active, setActive] = useState<boolean>(false)

  // Keep the latest callbacks/phrases addressable from inside the
  // long-lived recognition handlers (avoid stale closures).
  const onWakeRef = useRef(onWake)
  const phrasesRef = useRef(phrases)
  const onErrorRef = useRef(onError)
  useEffect(() => { onWakeRef.current = onWake }, [onWake])
  useEffect(() => { phrasesRef.current = phrases }, [phrases])
  useEffect(() => { onErrorRef.current = onError }, [onError])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const SR =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition ||
      null
    setSupported(!!SR)
  }, [])

  const stop = useCallback(() => {
    wantRunningRef.current = false
    const rec = recRef.current
    recRef.current = null
    if (rec) {
      try { rec.abort() } catch { /* ignore */ }
    }
    setActive(false)
  }, [])

  const start = useCallback(() => {
    if (typeof window === 'undefined') return
    const SR: SRCtor | null =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition ||
      null
    if (!SR) return
    if (recRef.current) return // already running

    let rec: SRInstance
    try {
      rec = new SR()
    } catch (e) {
      onErrorRef.current?.(`Wake-word init failed: ${(e as Error).message}`)
      return
    }
    rec.lang = lang
    rec.continuous = true
    rec.interimResults = true
    rec.maxAlternatives = 1

    rec.onstart = () => setActive(true)
    rec.onend = () => {
      setActive(false)
      recRef.current = null
      // Browser auto-ends after silence — re-arm if still wanted.
      if (wantRunningRef.current) {
        // microsleep before restart so we don't burn CPU on a tight loop.
        setTimeout(() => {
          if (wantRunningRef.current) start()
        }, 250)
      }
    }
    rec.onerror = (ev: Event) => {
      const err = (ev as any).error || 'unknown'
      // 'no-speech' / 'aborted' are routine; anything else surfaces.
      if (err !== 'no-speech' && err !== 'aborted') {
        onErrorRef.current?.(`Wake-word error: ${err}`)
      }
      // 'not-allowed' / 'service-not-allowed' = user denied mic. Don't loop.
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        wantRunningRef.current = false
      }
    }
    rec.onresult = (ev: SREvent) => {
      // Only inspect *final* results. Interim partials trigger false-positives.
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i]
        if (!result.isFinal) continue
        const transcript = result[0]?.transcript ?? ''
        const rest = matchWake(transcript, phrasesRef.current)
        if (rest !== null) {
          // Stop the listener so it doesn't keep firing while Arc handles
          // the request. Caller decides when (if ever) to re-enable.
          try { rec.stop() } catch { /* ignore */ }
          onWakeRef.current(rest)
          return
        }
      }
    }

    recRef.current = rec
    wantRunningRef.current = true
    try {
      rec.start()
    } catch (e) {
      onErrorRef.current?.(`Wake-word start failed: ${(e as Error).message}`)
      recRef.current = null
      wantRunningRef.current = false
    }
  }, [lang])

  // Drive start/stop from `enabled` + page visibility.
  useEffect(() => {
    if (!enabled) {
      stop()
      return
    }
    start()
    const onVisibility = () => {
      if (document.hidden) stop()
      else if (enabled) start()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      stop()
    }
  }, [enabled, start, stop])

  return { supported, active }
}
